import { useMemo } from 'react';
import { budgetStatus, legacyBudgetLimits, rolloverCarry, rule503020, type BudgetStatus } from '../../../finance';
import { useData } from '../../store/data';

export const monthKey = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;
export type BudgetSource = 'envelope' | 'legacy' | 'none';

/**
 * Ngân sách của một tháng: ưu tiên phong bì (budgetEnvelopes); chưa có thì ĐỌC ngân sách cũ
 * (`budgets`, không ghi đè) để dữ liệu cũ hiện đúng ngay. Rollover chỉ một cấp: phần dư tháng trước.
 */
export const useBudgetMonth = (year: number, month: number) => {
  const { budgetEnvelopes, budgets, expenses, plannedExpenses } = useData(s => s.data);
  return useMemo(() => {
    const limitsOf = (y: number, m: number) => {
      const env = budgetEnvelopes.find((e: any) => e.id === monthKey(y, m));
      if (env) return { source: 'envelope' as BudgetSource, limits: (env.limits ?? {}) as Record<string, number>, rollover: !!env.rollover };
      const legacy = budgets.find((b: any) => b.year === y && b.month === m);
      return legacy
        ? { source: 'legacy' as BudgetSource, limits: legacyBudgetLimits(legacy), rollover: false }
        : { source: 'none' as BudgetSource, limits: {} as Record<string, number>, rollover: false };
    };
    const cur = limitsOf(year, month);
    let carry: Record<string, number> = {};
    if (cur.rollover) {
      const prevD = new Date(year, month - 2, 1);
      const prev = limitsOf(prevD.getFullYear(), prevD.getMonth() + 1);
      if (prev.source !== 'none') {
        carry = rolloverCarry(budgetStatus({ limits: prev.limits, year: prevD.getFullYear(), month: prevD.getMonth() + 1, expenses }));
      }
    }
    const status: BudgetStatus = budgetStatus({ limits: cur.limits, carry, year, month, expenses, planned: plannedExpenses });
    return { key: monthKey(year, month), ...cur, carry, status };
  }, [budgetEnvelopes, budgets, expenses, plannedExpenses, year, month]);
};

export const useRule503020 = (year: number, month: number) => {
  const { incomes, expenses, expenseCategories } = useData(s => s.data);
  return useMemo(() => rule503020({
    incomes, expenses, year, month,
    classByName: Object.fromEntries(expenseCategories.map((c: any) => [c.name, c.budgetClass === 'need' ? 'need' : 'want']))
  }), [incomes, expenses, expenseCategories, year, month]);
};
